import type {MouseEventHandler, ReactElement} from 'react';
import React, {useRef} from 'react';
import type {AddAiItemDropdownPopupContentProps} from './add-ai-item-dropdown-popup-content.types';
import {AddAiItemPopUpSubPanel} from './add-ai-item-dropdown-popup-content.types';
import styles from './add-ai-item-dropdown-popup-content.module.scss';
import {AddAiItemPopOverContentState} from '@Components/add-ai-item-button/add-ai-item-button.types';
import {ControlledList} from '@Components/controlled-list';
import AiImagePanel from '@Panels/ai-image-panel';
import {onAddItems} from '@Components/poster-editor/library/poster-editor-open-modals';
import AiSubtitlePanel from '@Panels/ai-subtitle-panel';
import type {ControlledListItemType} from '@Components/controlled-list/controlled-list.types';
import {CONTROLLED_LIST_ITEM_TYPE} from '@Components/controlled-list/controlled-list.types';
import {Icon} from '@Components/icon-v2';
import {IconSize, IconType} from '@Components/icon-v2/icon.types';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import {AddItemThumbnail} from '@Components/poster-editor/components/poster-add-item-bar/components/add-item-thumbnail';
import {ActivityIndicatorType} from '@Components/activity-indicator/activity-indicator.types';
import {useAppSelector} from '@/hooks';
import {getActivityIndicatorTypeForAiImage, getActivityIndicatorTypeForAiSubtitle, getActivityIndicatorTypeForAiVoice} from '@Components/poster-editor/poster-editor-reducer';
import {getUserId, isUserPremium} from '@Libraries/user.library';
import AiVoicePanel from '@Panels/ai-voice-panel';
import {addGeneratedVoiceToDesign} from '@Libraries/text-to-speech.library';

export function AddAiItemDropdownPopupContent({...props}: AddAiItemDropdownPopupContentProps): ReactElement<any> {
  const isUserLoggedIn = useRef<boolean>(typeof getUserId() !== 'undefined');
  const isPremium = useRef(isUserLoggedIn.current && isUserPremium());

  const activityIndicatorForAiImage = useAppSelector(getActivityIndicatorTypeForAiImage);
  const activityIndicatorForAiSubtitle = useAppSelector(getActivityIndicatorTypeForAiSubtitle);
  const activityIndicatorForAiVoice = useAppSelector(getActivityIndicatorTypeForAiVoice);

  const onBack: MouseEventHandler | VoidFunction = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    props.onSubPanelBack();
  };

  const getItemsForAi = (): ControlledListItemType[] => {
    return [
      {
        id: 'ai-items-add-ai-image',
        pill: !isPremium.current ? (
          <Icon className={`spacing-m-l-1 spacing-p-t-1 ${styles.premiumCrown}`} size={IconSize.SIZE_ICON_16} type={IconType.SUCCESS} icon={'icon-crown'} />
        ) : undefined,
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
        text: window.i18next.t('pmwjs_ai_images'),
        subText: window.i18next.t('pmwjs_ai_images_list_item_desc'),
        onClick: (id, event): void => {
          event?.stopPropagation();
          event?.preventDefault();

          props.onClickAiImagesOption();
        },
        thumbnail: {
          type: ThumbnailType.CUSTOM,
          content: (
            <AddItemThumbnail
              icon="icon-ai-image"
              iconClassName={styles.aiListItemIconStyle}
              className={styles.aiListItemStyle}
              activityIndicatorType={
                props.showAsNewFeature.find((subpanel) => subpanel === AddAiItemPopUpSubPanel.AI_IMAGE) ? ActivityIndicatorType.NEW_PULSE : activityIndicatorForAiImage
              }
            />
          ),
        },
      },
      {
        id: 'ai-items-add-ai-subtitles',
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
        pill: !isPremium.current ? (
          <Icon className={`spacing-m-l-1 spacing-p-t-1 ${styles.premiumCrown}`} size={IconSize.SIZE_ICON_16} type={IconType.SUCCESS} icon={'icon-crown'} />
        ) : undefined,
        text: window.i18next.t('pmwjs_ai_subtitles'),
        subText: window.i18next.t('pmwjs_ai_subtitles_list_item_desc'),
        onClick: (id, event): void => {
          event?.stopPropagation();
          event?.preventDefault();

          props.onClickAiSubtitlesOption();
        },
        thumbnail: {
          type: ThumbnailType.CUSTOM,
          content: (
            <AddItemThumbnail
              icon="icon-ai-subtitles"
              iconClassName={styles.aiListItemIconStyle}
              className={styles.aiListItemStyle}
              activityIndicatorType={
                props.showAsNewFeature.find((subpanel) => subpanel === AddAiItemPopUpSubPanel.AI_SUBTITLE) ? ActivityIndicatorType.NEW_PULSE : activityIndicatorForAiSubtitle
              }
            />
          ),
        },
      },
      {
        id: 'ai-items-add-ai-voice',
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
        pill: !isPremium.current ? (
          <Icon className={`spacing-m-l-1 spacing-p-t-1 ${styles.premiumCrown}`} size={IconSize.SIZE_ICON_16} type={IconType.SUCCESS} icon={'icon-crown'} />
        ) : undefined,
        text: window.i18next.t('pmwjs_ai_voice'),
        subText: window.i18next.t('pmwjs_ai_voice_list_item_desc'),
        onClick: (id, event): void => {
          event?.stopPropagation();
          event?.preventDefault();

          props.onClickAiVoiceOption();
        },
        thumbnail: {
          type: ThumbnailType.CUSTOM,
          content: (
            <AddItemThumbnail
              icon="icon-ai-voice"
              iconClassName={styles.aiListItemIconStyle}
              className={styles.aiListItemStyle}
              activityIndicatorType={
                props.showAsNewFeature.find((subpanel) => subpanel === AddAiItemPopUpSubPanel.AI_VOICE) ? ActivityIndicatorType.NEW_PULSE : activityIndicatorForAiVoice
              }
            />
          ),
        },
      },
      {
        id: 'ai-items-remove-background',
        type: CONTROLLED_LIST_ITEM_TYPE.DEFAULT_2,
        pill: !isPremium.current ? (
          <Icon className={`spacing-m-l-1 spacing-p-t-1 ${styles.premiumCrown}`} size={IconSize.SIZE_ICON_16} type={IconType.SUCCESS} icon={'icon-crown'} />
        ) : undefined,
        text: window.i18next.t('pmwjs_ai_background_remover'),
        subText: window.i18next.t('pmwjs_ai_remove_background_item_desc'),
        onClick: (id, event): void => {
          event?.stopPropagation();
          event?.preventDefault();

          props.onClickAiRemoveBgOption();
        },
        thumbnail: {
          type: ThumbnailType.CUSTOM,
          content: (
            <AddItemThumbnail
              icon="icon-remove-bg"
              iconClassName={styles.aiListItemIconStyle}
              className={styles.aiListItemStyle}
            />
          ),
        },
      },
    ];
  };

  const getPopupContent = (): ReactElement<any> => {
    switch (props.dropdownContentState) {
      case AddAiItemPopOverContentState.AI_ITEMS_DROPDOWN:
        return <ControlledList items={getItemsForAi()} />;
      case AddAiItemPopOverContentState.AI_IMAGE:
        return <AiImagePanel shouldLoadDataUrlImage onAddSelected={onAddItems} panelId={props.AiImagePanelId ?? 'AI-Image-Panel-Add-AI-Item'} onBack={onBack} />;
      case AddAiItemPopOverContentState.AI_SUBTITLES:
        return (
          <AiSubtitlePanel contentClasses={styles.aiSubtitlePanel} onDone={props.onDone} onBack={onBack} panelId={props.AiSubtitlePanelId ?? 'AI-Subtitle-Panel-Add-AI-Item'} />
        );
      case AddAiItemPopOverContentState.AI_VOICE:
        return (
          <AiVoicePanel
            isOpenedInPopOver
            panelId={props.AiVoicePanelId ?? 'AI-Voice-Panel-Add-AI-Item'}
            onBack={onBack}
            onAddAudioItem={async (item): Promise<void> => {
              props.onDone();
              await addGeneratedVoiceToDesign(Number(item.id));
            }}
          />
        );
      default:
        throw new Error(`Unhandled drop down content state for add ai item dropdown: ${props.dropdownContentState}`);
    }
  };

  return getPopupContent();
}
